import './style.scss'

import React, { Component } from 'react'
import debounce from 'lodash/debounce'
import {
    fetchStreetNames,
    fetchStreetNumbers,
    setCO,
    setFloor,
    setHousehold,
    setStreetAddress,
    setStreetNumber,
} from 'actions/AddressHelper'
import {
    findBestFloorMatch,
    findBestHouseholdMatch,
    findBestStreetNameMatch,
    findBestStreetNoMatch,
    formatFloor,
    formatHousehold,
    formatStreetName,
    formatStreetNo,
} from 'utilities/address'

import AddressInput from 'components/Address/AddressInput'
import InputHelp from 'components/UI/InputHelp'
import InputRow from 'components/UI/InputRow'
import PropTypes from 'prop-types'
import TextInput from 'components/UI/TextInput'
import { connect } from 'react-redux'
import { toReact } from 'utilities/formattedStringParser'
import { withTranslation } from 'react-i18next'

const FIELD_NAME_STREETNAME = 'streetname'
const FIELD_NAME_STREETNO = 'streetno'
const FIELD_NAME_FLOOR = 'floor'
const FIELD_NAME_HOUSEHOLD = 'flat'

export class AddressHelper extends Component {
    constructor(props) {
        super(props)
        this.state = {
            formAutoNavigate: false,
            navigateFromField: null,
        }
        this.delayedfetchStreetNames = debounce(this.fetchSteetNames, 200)
    }

    fetchSteetNames(event) {
        this.props.dispatch(fetchStreetNames(event))
    }

    onStreeNameChange(event) {
        this.delayedfetchStreetNames(event)
    }

    componentDidMount() {
        this.props.noFocus || this.streetInput.focus()
    }

    componentDidUpdate() {
        const { formAutoNavigate, navigateFromField } = this.state
        if (formAutoNavigate) {
            switch (navigateFromField) {
                case FIELD_NAME_STREETNAME:
                    this.setFocus(this.numberInput)
                    break
                case FIELD_NAME_STREETNO:
                    this.setFocus(this.floorInput)
                    break
                case FIELD_NAME_FLOOR:
                    this.setFocus(this.householdInput)
                    break
            }
        }
    }

    componentWillReceiveProps(nextProps) {
        if ((!this.props.isFinished || this.props.co !== nextProps.co) && nextProps.isFinished) {
            this.handleComplete(nextProps)
        }
    }

    setAutoNavigate(formAutoNavigate, navigateFromField) {
        // Must exit a different field to update autonavigate
        const update = !(
            this.state.formAutoNavigate && this.state.navigateFromField === navigateFromField
        )

        if (update) {
            this.setState({
                formAutoNavigate,
                navigateFromField,
            })
        }
    }

    resetAutoNavigate() {
        this.setState({
            formAutoNavigate: false,
            navigateFromField: null,
        })
    }

    setFocus(inputField) {
        if (inputField && !inputField.contains(document.activeElement)) {
            inputField.focus()
        }
    }

    handleComplete(nextProps) {
        const {
            deliveryPointId,
            streetName,
            streetNo,
            entrance,
            floorNo,
            floorType,
            flatNo,
            co,
            zip,
            city,
            countryCode,
        } = nextProps
        this.props.onComplete({
            deliveryPointId,
            streetName,
            streetNo,
            entrance,
            floorNo,
            floorType,
            flatNo,
            co,
            zip,
            city,
            countryCode,
        })
    }

    render() {
        const {
            dispatch,
            streets,
            numbers,
            floors,
            households,
            zip,
            city,
            streetIds,
            floorType,
            floorNo,
            co,
            addressId,
            isFinished,
            unknownAddress,
            t,
        } = this.props
        const floorAndFlatHidden = !(
            (floors && floors.length > 0) ||
            (households && households.length > 0)
        )
        return (
            <div className="address-helper">
                <InputRow>
                    <AddressInput
                        onChange={this.onStreeNameChange.bind(this)}
                        className="address-helper__street"
                        inputRef={(el) => (this.streetInput = el)}
                        items={streets}
                        name={FIELD_NAME_STREETNAME}
                        label={t('addressHelperStreetLabel')}
                        fillHelp={toReact(t('addressHelperStreetFillHelp'))}
                        value={this.props.searchValues['streetname']}
                        view={formatStreetName}
                        findBestMatchIndex={findBestStreetNameMatch}
                        onComplete={(street, name, autoNavigate, wasTab) => {
                            this.setAutoNavigate(autoNavigate && !wasTab, name)
                            dispatch(setStreetAddress(street))
                        }}
                        onFocus={() => this.resetAutoNavigate()}
                        required
                        isActive={true}
                    />
                    <AddressInput
                        inputRef={(el) => (this.numberInput = el)}
                        className="address-helper__number"
                        items={numbers}
                        name={FIELD_NAME_STREETNO}
                        value={this.props.searchValues['streetno']}
                        label={t('addressHelperStreetNoLabel')}
                        view={formatStreetNo}
                        findBestMatchIndex={findBestStreetNoMatch}
                        onChange={(e) => {
                            dispatch(fetchStreetNumbers(e, streetIds))
                        }}
                        onComplete={(number, name, autoNavigate) => {
                            this.setAutoNavigate(autoNavigate, name)
                            dispatch(setStreetNumber(number))
                        }}
                        onFocus={() => this.resetAutoNavigate()}
                        required={!isFinished}
                        isActive={true}
                    />
                </InputRow>
                {!floorAndFlatHidden && (
                    <InputRow>
                        <AddressInput
                            inputRef={(el) => (this.floorInput = el)}
                            items={floors || []}
                            label={t('addressHelperFloorLabel')}
                            name={FIELD_NAME_FLOOR}
                            value={this.props.searchValues['floor'] || ''}
                            view={(item) => formatFloor(t, item)}
                            findBestMatchIndex={(items, value) =>
                                findBestFloorMatch(t, items, value)
                            }
                            onComplete={(floor, name, autoNavigate) => {
                                this.setAutoNavigate(autoNavigate, name)
                                dispatch(setFloor(addressId, floor))
                            }}
                            onFocus={() => this.resetAutoNavigate()}
                            required={floors && floors.length > 0}
                            isActive={floors && floors.length > 0}
                        />
                        <AddressInput
                            inputRef={(el) => (this.householdInput = el)}
                            items={households || []}
                            name={FIELD_NAME_HOUSEHOLD}
                            value={this.props.searchValues['flat'] || ''}
                            label={t('addressHelperFlatLabel')}
                            view={(household) =>
                                formatHousehold(t, floorType, floorNo, household.flatNo)
                            }
                            findBestMatchIndex={(items, value) =>
                                findBestHouseholdMatch(t, items, floorType, floorNo, value)
                            }
                            onComplete={(household, name) => {
                                this.setAutoNavigate(false, name)
                                dispatch(setHousehold(household))
                            }}
                            onFocus={() => this.resetAutoNavigate()}
                            required={households && households.length > 0}
                            isActive={households && households.length > 0}
                        >
                            <InputHelp
                                onClick={() =>
                                    dispatch({ type: 'TOGGLE_APARTMENT_NUMBER_HELP_MODAL' })
                                }
                                onClose={() =>
                                    dispatch({ type: 'TOGGLE_APARTMENT_NUMBER_HELP_MODAL' })
                                }
                                show={this.props.showApartmentNumberHelpModal}
                            >
                                <h2>{t('addressHelperFlatHelperTitle')}</h2>
                                <p>{t('addressHelperFlatHelperDescription1')}</p>
                                <img
                                    style={{ maxWidth: 200 }}
                                    src="https://di.no/addresshelper/diah_flat_illustration.png"
                                />
                                <p>{t('addressHelperFlatHelperDescription2')}</p>
                            </InputHelp>
                        </AddressInput>
                    </InputRow>
                )}
                {zip && (
                    <InputRow>
                        <TextInput
                            className="address-helper__zip"
                            value={zip}
                            label={t('addressHelperZipLabel')}
                            disabled
                        />
                        <TextInput
                            className="address-helper__city"
                            value={city}
                            label={t('addressHelperCityLabel')}
                            disabled
                        />
                    </InputRow>
                )}
                {unknownAddress && (
                    <span className="address-helper__unknown">
                        {t('addressHelperUnknownAddress')}
                    </span>
                )}
                <TextInput
                    type="textarea"
                    value={co}
                    maxLength={255}
                    onChange={(e) => {
                        dispatch(setCO(e))
                    }}
                    label={t('formsPersonalInfoFormCO')}
                />
            </div>
        )
    }
}

AddressHelper.propTypes = {
    showApartmentNumberHelpModal: PropTypes.bool,
    streets: PropTypes.array,
    numbers: PropTypes.array,
    floors: PropTypes.array,
    households: PropTypes.array,
    addressId: PropTypes.number,
    zip: PropTypes.string,
    city: PropTypes.string,
    deliveryPointId: PropTypes.number,
    isFinished: PropTypes.bool,
    unknownAddress: PropTypes.bool,
    streetIds: PropTypes.arrayOf(PropTypes.number),
    onComplete: PropTypes.func,
    dispatch: PropTypes.func,
    streetName: PropTypes.string,
    streetNo: PropTypes.number,
    entrance: PropTypes.string,
    floorNo: PropTypes.number,
    floorType: PropTypes.string,
    flatNo: PropTypes.number,
    co: PropTypes.string,
    countryCode: PropTypes.string,
    formAutoNavigate: PropTypes.bool,
    navigateFromField: PropTypes.string,
    searchValues: PropTypes.object,
    noFocus: PropTypes.bool,
    t: PropTypes.func,
}

const mapStateToProps = ({ addressHelper, ui }) => {
    const addressId = addressHelper.streetNo ? addressHelper.addressId : null
    return {
        showApartmentNumberHelpModal: ui.showApartmentNumberHelpModal,
        searchValues: addressHelper.searchValues,
        streets: addressHelper.streets,
        numbers: addressHelper.numbers,
        floors: addressHelper.floors,
        households: addressHelper.households,
        addressId: addressId,
        zip: addressHelper.zip,
        deliveryPointId: addressHelper.deliveryPointId,
        streetIds: addressHelper.streetIds,
        isFinished: addressHelper.isFinished,
        unknownAddress: addressHelper.unknownAddress,
        city: addressHelper.city,
        streetName: addressHelper.streetName,
        streetNo: addressHelper.streetNo,
        entrance: addressHelper.entrance,
        floorNo: addressHelper.floorNo,
        floorType: addressHelper.floorType,
        flatNo: addressHelper.flatNo,
        co: addressHelper.co,
        countryCode: addressHelper.countryCode,
        formAutoNavigate: addressHelper.formAutoNavigate,
        navigateFromField: addressHelper.navigateFromField,
    }
}

export default connect(mapStateToProps)(withTranslation()(AddressHelper))
